/**
 * Cart validation. Runs on every page, not just /cart, because most themes
 * put the cart in a drawer.
 *
 * Blocks checkout when:
 *   - no postcode has been checked yet
 *   - the postcode is outside every delivery area
 *   - the cart mixes items from more than one depot
 *   - an item's depot isn't the customer's depot
 *
 * Re-runs whenever the cart changes (add / change / update / clear), by
 * watching Shopify's own cart endpoints.
 */
(function () {
  'use strict';

  var depotNames = [];
  var running = false;
  var pending = false;

  function escapeHtml(value) {
    return String(value == null ? '' : value).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  /* ---------------------------------------------------------- cart reading */

  /** Prefer the structured option values; fall back to the variant title. */
  function itemDepot(item) {
    var pairs = item.options_with_values || [];

    for (var i = 0; i < pairs.length; i++) {
      if (depotNames.indexOf(String(pairs[i].value).trim()) > -1) {
        return String(pairs[i].value).trim();
      }
    }

    // Match on whole option segments so a depot name can't match inside a word.
    var parts = String(item.variant_title || '').split(/\s*\/\s*/).map(function (p) {
      return p.trim();
    });

    for (var j = 0; j < depotNames.length; j++) {
      if (parts.indexOf(depotNames[j]) > -1) return depotNames[j];
    }

    return null;
  }

  function validate(cart) {
    if (!cart || !cart.items || cart.items.length === 0) {
      return { valid: true };
    }

    var state = window.DepotChecker.state();

    if (state === 'unset') return { valid: false, type: 'no_depot' };
    if (state === 'outside') return { valid: false, type: 'outside_area' };

    var selected = window.DepotChecker.get();
    var found = {};
    var mismatched = [];

    cart.items.forEach(function (item) {
      var depot = itemDepot(item);
      if (!depot) return;

      found[depot] = true;

      if (depot !== selected) {
        mismatched.push({ title: item.product_title || item.title, depot: depot });
      }
    });

    var depotList = Object.keys(found);

    if (depotList.length > 1) {
      return { valid: false, type: 'mixed', depots: depotList };
    }

    if (mismatched.length > 0) {
      return { valid: false, type: 'wrong_depot', selectedDepot: selected, mismatched: mismatched };
    }

    return { valid: true };
  }

  /* -------------------------------------------------------------- warnings */

  function messageFor(result) {
    if (result.type === 'no_depot') {
      return '<strong>Before continuing, please check if we deliver to your area.</strong>' +
        '<p><button type="button" class="depot-cart-warning__action" data-depot-checker-open>' +
        'Use our delivery area checker</button></p>';
    }

    if (result.type === 'outside_area') {
      return '<strong>Sorry, we do not currently deliver to your area.</strong>' +
        '<p>Your order cannot be accepted.</p>' +
        '<p><button type="button" class="depot-cart-warning__action" data-depot-checker-open>' +
        'Check a different postcode</button></p>';
    }

    if (result.type === 'mixed') {
      return '<strong>Depot conflict</strong>' +
        '<p>Your basket contains items from more than one depot: ' +
        escapeHtml(result.depots.join(', ')) + '.</p>' +
        '<p>Please remove items so everything comes from the same depot.</p>';
    }

    var items = result.mismatched.map(function (m) {
      return '<li><strong>' + escapeHtml(m.title) + '</strong> (from ' + escapeHtml(m.depot) + ')</li>';
    }).join('');

    return '<strong>Depot mismatch</strong>' +
      '<p>You are not in the delivery area for at least one item in your basket. ' +
      'Please order products from the <strong>' + escapeHtml(result.selectedDepot) +
      '</strong> depot.</p>' +
      '<ul>' + items + '</ul>' +
      '<p><button type="button" class="depot-cart-warning__action" data-depot-checker-open>' +
      'Change my postcode</button></p>';
  }

  /** Every plausible place a theme renders cart contents. */
  function warningTargets() {
    var selectors = [
      'cart-drawer form', 'cart-drawer',
      '#CartDrawer', '.cart-drawer', '.drawer__inner',
      'form[action="/cart"]', 'form[action^="/cart"]',
      '#cart-mini', '.cart__items', '[data-cart-items]', '.cart'
    ];

    // Deliberately includes containers that are currently hidden - a closed
    // cart drawer still needs the warning waiting inside it when it opens.
    var targets = [];
    selectors.forEach(function (selector) {
      document.querySelectorAll(selector).forEach(function (el) {
        // Skip anything nested inside a target we've already picked.
        if (targets.some(function (t) { return t.contains(el) || el.contains(t); })) return;
        targets.push(el);
      });
    });

    return targets;
  }

  function clearWarnings() {
    document.querySelectorAll('.depot-cart-warning').forEach(function (el) { el.remove(); });
  }

  function showWarning(result) {
    clearWarnings();

    var html = messageFor(result);
    var targets = warningTargets();

    if (targets.length === 0) return;

    targets.forEach(function (target) {
      var warning = document.createElement('div');
      warning.className = 'depot-cart-warning';
      warning.innerHTML = html;
      target.insertBefore(warning, target.firstChild);
    });
  }

  /* ------------------------------------------------------ checkout buttons */

  function checkoutButtons() {
    return document.querySelectorAll(
      '[name="checkout"], ' +
      'button[type="submit"][form*="cart"], ' +
      'a[href$="/checkout"], a[href*="/checkout?"], ' +
      '.shopify-payment-button, ' +
      '[data-checkout-button]'
    );
  }

  function blockCheckout() {
    checkoutButtons().forEach(function (button) {
      button.classList.add('depot-blocked');
      if ('disabled' in button) button.disabled = true;
      button.setAttribute('aria-disabled', 'true');
    });
  }

  function unblockCheckout() {
    checkoutButtons().forEach(function (button) {
      button.classList.remove('depot-blocked');
      if ('disabled' in button) button.disabled = false;
      button.removeAttribute('aria-disabled');
    });
  }

  /* ------------------------------------------------------------------ main */

  function run() {
    // A cart change that lands mid-check must not be dropped - remember it and
    // re-run once the in-flight check finishes.
    if (running) {
      pending = true;
      return Promise.resolve();
    }
    running = true;

    return fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (res) { return res.json(); })
      .then(function (cart) {
        var result = validate(cart);

        if (result.valid) {
          clearWarnings();
          unblockCheckout();
        } else {
          showWarning(result);
          blockCheckout();
        }
      })
      .catch(function (err) {
        console.error('[depot-checker] cart validation failed', err);
      })
      .then(function () {
        running = false;
        if (pending) { pending = false; return run(); }
      });
  }

  /** Re-check after any cart mutation the theme makes. */
  function watchCartRequests() {
    var CART_WRITE = /\/cart\/(add|change|update|clear)/;
    var nativeFetch = window.fetch;

    window.fetch = function (input) {
      var url = typeof input === 'string' ? input : (input && input.url) || '';
      var promise = nativeFetch.apply(this, arguments);

      if (CART_WRITE.test(url)) {
        promise.then(function () { setTimeout(run, 150); }).catch(function () {});
      }

      return promise;
    };

    var nativeOpen = XMLHttpRequest.prototype.open;
    XMLHttpRequest.prototype.open = function (method, url) {
      if (CART_WRITE.test(String(url))) {
        this.addEventListener('load', function () { setTimeout(run, 150); });
      }
      return nativeOpen.apply(this, arguments);
    };

    // Theme / Shopify events, where they exist.
    ['cart:updated', 'cart:refresh', 'cart:change'].forEach(function (name) {
      document.addEventListener(name, function () { setTimeout(run, 150); });
    });

    document.addEventListener('depot:change', function () { run(); });
  }

  function init() {
    if (!window.DepotChecker) return;

    window.DepotChecker.ready.then(function (lookup) {
      if (!lookup) return;   // coverage data unavailable - don't block anyone

      depotNames = window.DepotChecker.depotNames();
      watchCartRequests();
      run();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
